import { Injectable, OnDestroy } from '@angular/core';
import { EventsRequest } from '@aws-sdk/client-pinpoint';
import pkg from '../../../../package.json';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { UserLocationState } from '../../store/user-location.state';
import { HttpClientService } from '../http-client.service';
import { UserState, UserType } from '../../store/user.state';
import { StoredProject } from '../../models/projects.model';
import { DefaultProjectState } from '../../store/default-project.state';
import { AnalyticsEventType, EventAttributesMapper, EventType, PageViewEvents } from './analytics.model';
import { Profile } from '../../models/profile.model';
import { LanguageState } from '../../store/language.state';
import { GA4Service } from './ga4.service';
import { filter, Subject, takeUntil } from 'rxjs';
import { Feature, LoggerService } from '../logger.service';
import { MixpanelService } from '../mixpanel.service';
import { AppState } from '../../store/app.state';

@Injectable({ providedIn: 'root' })
export class AnalyticsService implements OnDestroy {
  private destroyed$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private httpClientService: HttpClientService,
    private ga4Service: GA4Service,
    private router: Router,
    private userState: UserState,
    private userLocationState: UserLocationState,
    private defaultProjectState: DefaultProjectState,
    private languageState: LanguageState,
    private loggerService: LoggerService,
    private mixpanelService: MixpanelService,
    private appState: AppState,
  ) {}

  initialize(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroyed$),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      )
      .subscribe(event => {
        const pageUrl = event.urlAfterRedirects.split('?')[0].split('#')[0];

        let screenName: PageViewEvents | null = null;

        switch (pageUrl) {
          case '/':
          case '/home':
            screenName = PageViewEvents.Home;
            break;
          case '/onboarding':
            screenName = PageViewEvents.Onboarding;
            break;
          case '/saved':
            screenName = PageViewEvents.Saved;
            break;
          case '/explore':
            screenName = PageViewEvents.Explore;
            break;
          case '/dashboard':
            screenName = PageViewEvents.Dashboard;
            break;
          case '/language':
            screenName = PageViewEvents.Language;
            break;
          case '/library-code':
            screenName = PageViewEvents.LibraryCode;
            break;
          case '/settings/delete-account':
            screenName = PageViewEvents.DeleteAccount;
            break;
          case '/settings/family':
            screenName = PageViewEvents.SettingsFamily;
            break;
          case '/how-to-use':
            screenName = PageViewEvents.HowToUse;
            break;
          case '/book-error':
            screenName = PageViewEvents.BookError;
            break;
          case '/tips':
            screenName = PageViewEvents.Tips;
            break;
            break;
          case '/authentication':
            screenName = PageViewEvents.AuthenticationEmailStep;
            break;
          case '/confirmation-email':
            screenName = PageViewEvents.AuthenticationConfirmEmailStep;
            break;
          case '/menu':
            screenName = PageViewEvents.Menu;
            break;
          case '/why-booksmart':
            screenName = PageViewEvents.WhyBooksmart;
            break;
          case '/settings':
            screenName = PageViewEvents.AccountSettings;
            break;
          case '/help':
            screenName = PageViewEvents.Help;
            break;
          case '/reset-password':
          case '/forgot-password':
            screenName = PageViewEvents.AuthenticationResetPasswordLinkStep;
            break;
          default:
            if (pageUrl.startsWith('/shelf')) {
              screenName = PageViewEvents.Shelf;
            } else if (pageUrl.startsWith('/author')) {
              screenName = PageViewEvents.Author;
            } else if (pageUrl.startsWith('/publisher')) {
              screenName = PageViewEvents.Publisher;
            } else if (pageUrl.startsWith('/tips/category')) {
              screenName = PageViewEvents.TipsCategory;
            } else if (pageUrl.endsWith('/details')) {
              screenName = PageViewEvents.BookDetails;
            } else if (pageUrl.endsWith('/reader')) {
              screenName = PageViewEvents.BookReader;
            } else if (pageUrl.endsWith('/complete')) {
              screenName = PageViewEvents.BookComplete;
            } else if (pageUrl.endsWith('/reader/settings') || pageUrl.endsWith('/reader/settings/')) {
              screenName = PageViewEvents.BookReaderSettings;
            } else if (pageUrl.endsWith('/reader/font') || pageUrl.endsWith('/reader/font/')) {
              screenName = PageViewEvents.BookReaderFont;
            } else if (pageUrl.endsWith('/reader/table-of-contents') || pageUrl.endsWith('/reader/table-of-contents/')) {
              screenName = PageViewEvents.BookReaderTableOfContents;
            }
        }

        if (!screenName) {
          this.loggerService.error('AnalyticsService::initialize - Page not found', {
            feature: Feature.ANALYTICS,
            context: 'AnalyticsService::initialize - Page not found : ' + pageUrl,
          });
        } else {
          this.sendEvent(AnalyticsEventType.InScreen, { screenName });
        }
      });
  }

  sendEvent<T extends EventType>(eventType: T, attributes: EventAttributesMapper[T]): void {
    const defaultProject: StoredProject = this.defaultProjectState.defaultProject$.value;
    const userLocation = this.userLocationState.userLocation$.value;
    const url = this.router.url.split('?')[0]; // Ignoring QueryParams to avoid 200 characters limit on Pinpoint attribute
    const user = this.userState.user$.value;
    const clientId = this.httpClientService.getClientCodeAndToken().clientCode;
    const language = this.languageState.language$.value;
    const queryParams = JSON.stringify(this.appState.app$.value.queryParams); // Getting Query Params
    const utmParams = JSON.stringify(this.filterUtmParams(this.appState.app$.value.queryParams ?? {}));

    const globalAttributes = {
      // Org & Project
      organization_code: defaultProject.organizationCode,
      program_id: defaultProject.programId,
      project_id: defaultProject.id,
      project_name: defaultProject.name,
      project_code: defaultProject.code,

      // Application
      app_client_id: clientId,
      version_code: pkg.version, // Same from package.json
      title: pkg.name, // Same from package.json
      ui_language: language.selected,

      // User
      user_id: user.id ? user.id : user.id,
      guest_user_id: user.userType === UserType.GUEST ? user.id : user.guestCredentials?.id,
      ...(user.userType !== UserType.GUEST && { auth_provider: user.userType === 'user' ? 'worldreader' : user.userType }),
      ...(user.familyMetadata && { family_metadata: user.familyMetadata }),

      // Location (IP API)
      country: this.userLocationState.userLocation$.value.countryCode,
      administrative_area_1: userLocation!.regionName,
      administrative_area_2: userLocation!.district,
      city: userLocation!.city,
      postal_code: userLocation!.zip,

      // Device & Web
      user_agent: window.navigator.userAgent,
      url, // Current path
      ...(queryParams !== '{}' && { queryParams: queryParams }),
      ...(utmParams !== '{}' && { access_code: utmParams }),
      is_opera_mini_type: 'false', // set to True in this project
      event_datetime: new Date().toISOString(), // date time in UTC ISO standard ISO 8601
    };

    // eslint-disable-next-line
    const eventProperties: any = {
      AppPackageName: clientId,
      AppTitle: pkg.name,
      AppVersionCode: pkg.version,
      // The important
      EventType: eventType,
      // Adding custom attributes for each event and converting property names from camel to underscore
      Attributes: this.formatObjectForPinpoint({ ...globalAttributes, ...attributes }),
    };

    const pinPointObject: EventsRequest = {
      BatchItem: {
        '<keys>': {
          Endpoint: {
            Location: {
              Country: userLocation!.countryCode,
            },
            User: {
              UserAttributes: {
                '<keys>': [''],
              },
              UserId: user.id,
            },
          },
          Events: {
            [eventType]: eventProperties,
          },
        },
      },
    };

    this.http.post<void>(`${environment.usersBaseUrl}/events`, pinPointObject).subscribe({});

    // Send event to Mixpanel
    this.mixpanelService.track(eventProperties.EventType, this.formatMixPanelObject({ ...globalAttributes, ...attributes }));

    // Send page view events to google Analytics
    if (eventType === 'inScreen') {
      this.ga4Service.pushPageView(url);
    } else {
      this.ga4Service.pushCustomEvent(eventType, url, attributes);
    }
  }

  // eslint-disable-next-line
  formatMixPanelObject(obj: any): any{
    const newObj: Record<string, string> = {};
    let numberOfAttributes = 0;

    for (const key in obj) {

      if (Object.prototype.hasOwnProperty.call(obj, key) && numberOfAttributes < 40) {
        const underscoreKey = key.replace(/([a-z])([A-Z])/g, '$1_$2')?.toLowerCase(); // Convert camelCase to lowercase with underscores.
        newObj[underscoreKey] = obj[key]; // Assign the value to the new key.

        // Make sure that the value is not empty
        if (newObj[underscoreKey] === '') {
          delete newObj[underscoreKey];
          continue;
        }
        numberOfAttributes++;
      }
    }
    return newObj;
  }

  // TODO To be removed after Pinpoint deprecation
  // eslint-disable-next-line
  formatObjectForPinpoint(obj: any): any {
    const newObj: Record<string, string> = {};
    let numberOfAttributes = 0;

    for (const key in obj) {

       if (Object.prototype.hasOwnProperty.call(obj, key) && numberOfAttributes < 40) {
        const underscoreKey = key.replace(/([a-z])([A-Z])/g, '$1_$2')?.toLowerCase(); // Convert camelCase to lowercase with underscores.
        newObj[underscoreKey] = obj[key]; // Assign the value to the new key.

        // Make sure that the value is not empty
        if (newObj[underscoreKey] === '') {
          delete newObj[underscoreKey];
          continue;
        }

        // Also convert numbers into strings as Pinpoint doesn't like numbers
        if (typeof newObj[underscoreKey] === 'number') {
          newObj[underscoreKey] = (newObj[underscoreKey] as number).toString();
        }

        // Make sure the value is not longer than 200 chars
        if (newObj[underscoreKey]?.length > 200) {
          newObj[underscoreKey] = newObj[underscoreKey].slice(0, 200);
        }

        // Don't send family_metadata
        if (underscoreKey === 'family_metadata') {
          delete newObj[underscoreKey];
        }

        numberOfAttributes++;
      }
    }
    return newObj;
  }

  filterUtmParams(obj: Record<string, string>): Record<string, string> {
    return Object.keys(obj)
      .filter(key => key.startsWith('utm_'))
      .reduce(
        (acc, key) => {
          acc[key] = obj[key];
          return acc;
        },
        {} as Record<string, string>,
      );
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}